import { IconType } from 'react-icons';
import {
    MdOutlineAlarm,
    MdOutlineAltRoute,
    MdOutlineAssignmentTurnedIn,
    MdOutlineAutorenew,
    MdOutlineCheckCircle,
    MdOutlineDescription,
    MdOutlineEventAvailable,
    MdOutlineFlag,
    MdOutlineHistoryToggleOff,
    MdOutlineMenuBook,
    MdOutlinePendingActions,
    MdOutlineReportProblem,
    MdOutlineSchedule,
    MdOutlineShield,
    MdOutlineSupportAgent,
    MdOutlineSwapHoriz,
    MdOutlineVerified,
    MdOutlineVisibility,
} from 'react-icons/md';

/**
 * Единая палитра категорий приложения — общий источник цвета и иконки для виджета
 * Календарь (повестка) и дропдауна/тостов уведомлений. Один цвет = один смысл.
 *
 * Уведомления разложены по типу работы (из роли в теге, см. `notificationCategory`):
 * Поручение / Согласование / Утверждение / Ознакомление / Контроль, плюс событийные
 * Срок / Просрочено / Ход исполнения / Маршрут / Документ / Исключение / Напоминание / Доступ поддержки /
 * Замещение / Фоновые задачи / Документация.
 * Календарь использует только `task`/`reminder`/`cp`/`meeting`; их CSS-токены также
 * подключены в `agenda.scss`. Иконки — тонкие контурные (Material Outlined).
 */
export type CategoryKey =
    | 'task' // Поручение (assignment.mainperformer/coperformer) ≡ задача Календаря
    | 'reminder' // Напоминание (MailReminder)
    | 'cp' // КП — только Календарь
    | 'meeting' // Встреча / КПЗ — только Календарь
    | 'approval' // Согласование
    | 'confirmation' // Утверждение
    | 'review' // Ознакомление
    | 'control' // Контроль (наблюдение/надзор)
    | 'deadline' // Срок (приближается/наступил)
    | 'overdue' // Просрочено
    | 'progress' // Ход исполнения поручения
    | 'route' // Событие маршрута/процесса
    | 'doc' // Событие/роль по документу
    | 'exception' // Обработка исключения
    | 'support' // Доступ поддержки (вход «от имени»), тег support.*
    | 'substitution' // Замещение: назначение и снятие заместителя или уполномоченного, тег substitution.*
    | 'job' // Фоновые задачи: итоги загрузок из файла (job.*) и построения отчётов (report.*)
    | 'documentation'; // Документация: новый или обновлённый документ раздела «Документация», тег documentation.*

export interface CategoryToken {
    /** Базовый цвет категории (CSS-токен либо hex). Точка, иконка, акцент. */
    color: string;
    /** Иконка категории (react-icons/md, контурная). */
    icon: IconType;
    /** Человекочитаемая метка (ru) для чипа в дропдауне/тосте уведомлений. */
    label: string;
}

/**
 * CSS-токены имеют согласованные светлый и тёмный варианты в `colors.scss`, поэтому
 * один ключ остаётся читаемым и одинаковым по смыслу в Календаре, уведомлениях и
 * настройках. Близкие по оттенку пары дополнительно разводит иконка.
 */
export const CATEGORY_TOKENS: Record<CategoryKey, CategoryToken> = {
    task: { color: 'var(--category-task)', icon: MdOutlineAssignmentTurnedIn, label: 'Поручение' },
    reminder: { color: 'var(--category-reminder)', icon: MdOutlineAlarm, label: 'Напоминание' },
    cp: { color: 'var(--category-cp)', icon: MdOutlineFlag, label: 'КП' },
    meeting: { color: 'var(--category-meeting)', icon: MdOutlineEventAvailable, label: 'Встреча' },
    approval: { color: 'var(--category-approval)', icon: MdOutlineCheckCircle, label: 'Согласование' },
    confirmation: { color: 'var(--category-confirmation)', icon: MdOutlineVerified, label: 'Утверждение' },
    review: { color: 'var(--category-review)', icon: MdOutlineVisibility, label: 'Ознакомление' },
    control: { color: 'var(--category-control)', icon: MdOutlineShield, label: 'Контроль' },
    deadline: { color: 'var(--category-deadline)', icon: MdOutlineSchedule, label: 'Срок' },
    overdue: { color: 'var(--category-overdue)', icon: MdOutlineHistoryToggleOff, label: 'Просрочено' },
    progress: { color: 'var(--category-progress)', icon: MdOutlineAutorenew, label: 'Ход исполнения' },
    route: { color: 'var(--category-route)', icon: MdOutlineAltRoute, label: 'Маршрут' },
    doc: { color: 'var(--category-doc)', icon: MdOutlineDescription, label: 'Документ' },
    exception: { color: 'var(--category-exception)', icon: MdOutlineReportProblem, label: 'Исключение' },
    support: { color: 'var(--category-support)', icon: MdOutlineSupportAgent, label: 'Доступ поддержки' },
    substitution: { color: 'var(--category-substitution)', icon: MdOutlineSwapHoriz, label: 'Замещение' },
    job: { color: 'var(--category-job)', icon: MdOutlinePendingActions, label: 'Фоновые задачи' },
    documentation: { color: 'var(--category-documentation)', icon: MdOutlineMenuBook, label: 'Документация' },
};
